import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import StarRating from '../common/StarRating';
import Avatar from '../common/Avatar';
import { formatAmsterdamDate } from '../../utils/formatAmsterdamDate';
import { API_BASE_URL } from '../../services/api';

const Card = styled(motion.div)<{ $clickable?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  transition: all 0.2s ease-in-out;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
  margin-bottom: 0.5rem;
  position: relative;
  overflow: hidden;

  &:hover {
    ${props => props.$clickable && `
      background-color: #f8f9fa;
      border-color: #d1d5db;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      transform: translateY(-1px);
    `}
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: var(--primary-orange, #f97316);
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover::before {
    ${props => props.$clickable && 'opacity: 1;'}
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 0.75rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  width: 100%;
`;

const NameContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
  min-width: 100px;
`;

const Name = styled.span`
  font-weight: 700;
  color: #202124;
  font-size: 0.95rem;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Meta = styled.span`
  font-size: 0.75rem;
  color: #70757a;
  white-space: nowrap;
  line-height: 1.4;
  margin-top: 0.2rem;
`;

const Comment = styled.div<{ $isExpanded?: boolean }>`
  font-size: 0.85rem;
  color: #3c4043;
  line-height: 1.4;
  margin: 0;
  text-align: right;
  
  ${props => props.$isExpanded ? `
    display: block;
    width: 100%;
  ` : `
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  `}
`;

interface ReviewCardProps {
  review: {
    id: string | number;
    studentRating: number;
    studentRatingComment?: string | null;
    lessonDateTime?: string;
    dateTime?: string;
    student?: {
      id?: string | number;
      firstName?: string;
    } | null;
    studentFirstName?: string | null;
    studentName?: string | null;
    studentId?: string | number;
    lessonId?: string | number;
  };
  onClick?: () => void;
  idx?: number;
  isExpanded?: boolean;
}

const ReviewCard: React.FC<ReviewCardProps> = ({ review, onClick, idx = 0, isExpanded = false }) => {
  const firstName = review.studentFirstName || review.studentName || review.student?.firstName || 'Student';
  const initials = firstName.charAt(0).toUpperCase();
  const studentId = review.student?.id || review.studentId;
  const date = review.lessonDateTime || review.dateTime;

  return (
    <Card
      $clickable={!!onClick}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: idx * 0.05 }}
      onClick={onClick}
    >
      <Header>
        <Avatar 
          size={36} 
          src={studentId ? `${API_BASE_URL}/api/users/${studentId}/avatar` : null} 
          initials={initials} 
        />
        <Content>
          <TopRow>
            <NameContainer>
              <Name>{firstName}</Name>
              <Meta>{date ? formatAmsterdamDate(date) : ''}</Meta>
            </NameContainer>
            <RightColumn>
              <StarRating value={review.studentRating} readOnly size={14} />
              <Comment $isExpanded={isExpanded}>
                {review.studentRatingComment || 'No comment'}
              </Comment>
            </RightColumn>
          </TopRow>
        </Content>
      </Header>
    </Card>
  );
};

export default ReviewCard;
